import { useState, useMemo, useRef, useEffect, lazy, Suspense } from "react";
import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AutocompleteSelect from "@/components/ui/autocomplete-select";
import { COUNTRIES } from "@/constants/countries";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Search, Loader2, MapPin, Users, X, Star, ChevronLeft, ChevronRight,
  RotateCcw, ChevronDown, Minus, Plus, SlidersHorizontal, Globe,
} from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { CityAutocomplete, type CityAutocompletePlace } from "../../shared/CityAutocomplete";
import { SortResultsButton } from "../../shared/SortResultsButton";
import type {
  RateHawkSearchResult, RateHawkSearchFilter, RateHawkRoomGuestConfig, RateHawkSearchMode,
  RateHawkSearchSort, RateHawkSearchFacets, RateHawkPageSize,
} from "../types";
import { RATEHAWK_PAGE_SIZES } from "../types";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchResultCard, SearchResultCardSkeleton } from "./SearchResultCard";

// Lazy: leaflet + react-leaflet + il CSS pesano, e la maggior parte delle
// ricerche non apre mai il picker. Vite ne fa un chunk separato.
const CoordinatePickerMap = lazy(() => import("./CoordinatePickerMap"));

const MAX_ROOMS = 9;
const MAX_ADULTS_PER_ROOM = 20;
const MAX_CHILDREN_PER_ROOM = 10;
const DEFAULT_CHILD_AGE = 10;

/**
 * Etichette leggibili per i `kind` ETG, che arrivano in snake_case inglese.
 * La LISTA delle caselle non sta qui: si ricava dai risultati (vedi
 * availableKinds), come già succede per i servizi. Una lista hardcoded si era
 * scollata dai valori reali — "Villa", "Guest house" e "Motel" non matchavano
 * nulla, mentre BNB, Apart-hotel, Camping e altri non erano proponibili.
 * Un kind non mappato qui viene mostrato così com'è.
 */
const KIND_LABELS: Record<string, string> = {
  Hotel: "Hotel",
  Apartment: "Appartamento",
  Hostel: "Ostello",
  Resort: "Resort",
  Guesthouse: "Guest house",
  BNB: "B&B",
  "Apart-hotel": "Aparthotel",
  "Mini-hotel": "Mini hotel",
  Cottages_and_Houses: "Case e cottage",
  Villas_and_Bungalows: "Ville e bungalow",
  Boutique_and_Design: "Boutique & design",
  Farm: "Agriturismo",
  Camping: "Campeggio",
  Glamping: "Glamping",
  Castle: "Castello",
  Sanatorium: "Struttura termale",
};

const kindLabel = (k: string) => KIND_LABELS[k] ?? k;

const MEAL_OPTIONS: { value: string; label: string }[] = [
  { value: "nomeal", label: "Room only" },
  { value: "breakfast", label: "Breakfast" },
  { value: "halfboard", label: "Half board" },
  { value: "fullboard", label: "Full board" },
  { value: "all-inclusive", label: "All inclusive" },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface SearchWithResultsProps {
  cityName: string;
  setCityName: (v: string) => void;
  coordinates: { lat: number; lng: number } | null;
  setCoordinates: (v: { lat: number; lng: number } | null) => void;
  /** Modalità corrente, scelta dall'utente. Possiede lo stato il modal. */
  searchMode: RateHawkSearchMode;
  /** Destinazione scelta dall'autocomplete → modalità "region". */
  onSelectRegion: (place: CityAutocompletePlace) => void;
  /** Punto scelto sulla mappa → modalità "coordinate personalizzate". */
  onSelectCustomCoordinates: (coords: { lat: number; lng: number }) => void;
  /** Azzera destinazione e riporta alla modalità "region". */
  onClearDestination: () => void;
  checkIn: string;
  setCheckIn: (v: string) => void;
  checkOut: string;
  setCheckOut: (v: string) => void;
  rooms: RateHawkRoomGuestConfig[];
  setRooms: (v: RateHawkRoomGuestConfig[]) => void;
  radius: number;
  setRadius: (v: number) => void;
  filter: RateHawkSearchFilter;
  onFilterChange: (f: RateHawkSearchFilter) => void;
  /** Ordinamento server-side: cambiarlo riordina l'intera ricerca, non la pagina. */
  sort: RateHawkSearchSort;
  onSortChange: (s: RateHawkSearchSort) => void;
  /** Pagina corrente confermata dal server (1-based). */
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  pageSize: RateHawkPageSize;
  onPageSizeChange: (s: RateHawkPageSize) => void;
  /** Valori selezionabili nei filtri, calcolati sull'intero set di risultati. */
  facets: RateHawkSearchFacets;
  /** Strutture proponibili per questa ricerca, prima dei filtri. */
  totalHotels?: number | null;
  /** Strutture che superano i filtri correnti. */
  totalFiltered?: number | null;
  canSearch: boolean;
  isSearching: boolean;
  onSearch: () => void;
  results: RateHawkSearchResult[];
  searchError: string | null;
  onSelectResult: (result: RateHawkSearchResult) => void;
  startDate?: string | null;
  endDate?: string | null;
  residency: string;
  onResidencyChange: (v: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SearchWithResults({
  cityName,
  setCityName,
  coordinates,
  setCoordinates,
  searchMode,
  onSelectRegion,
  onSelectCustomCoordinates,
  onClearDestination,
  checkIn,
  setCheckIn,
  checkOut,
  setCheckOut,
  rooms,
  setRooms,
  radius,
  setRadius,
  filter,
  onFilterChange,
  sort,
  onSortChange,
  page,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  facets,
  totalHotels,
  totalFiltered,
  canSearch,
  isSearching,
  onSearch,
  results,
  searchError,
  onSelectResult,
  startDate,
  endDate,
  residency,
  onResidencyChange,
}: SearchWithResultsProps) {
  const { t } = useI18n();

  // Filtri, ordinamento e pagina non sono più stato locale: li possiede il
  // modal e li applica la edge function sull'intera ricerca cachata. Tenerli
  // qui significava filtrare e ordinare i soli risultati ricevuti — una
  // pagina, non il catalogo.
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const guestsRef = useRef<HTMLDivElement>(null);

  // Helpers to update individual server-side filter fields
  const setNameFilter = (v: string) =>
    onFilterChange({ ...filter, name: v || undefined });
  const setMinStarRating = (v: NonNullable<RateHawkSearchFilter["min_star_rating"]>) =>
    onFilterChange({ ...filter, min_star_rating: v === filter.min_star_rating ? undefined : v });
  const toggleKind = (k: string) => {
    const cur = filter.kind ?? [];
    onFilterChange({ ...filter, kind: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] });
  };
  const toggleMeal = (m: string) => {
    const cur = filter.meal_type ?? [];
    onFilterChange({ ...filter, meal_type: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m] });
  };

  // Per-room guest helpers — each room's adults/children are edited independently.
  const updateRoom = (index: number, patch: Partial<RateHawkRoomGuestConfig>) => {
    setRooms(rooms.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };
  const addRoom = () => {
    if (rooms.length >= MAX_ROOMS) return;
    setRooms([...rooms, { adults: 1, childrenAges: [] }]);
  };
  const removeRoom = (index: number) => {
    if (rooms.length <= 1) return;
    setRooms(rooms.filter((_, i) => i !== index));
  };
  const addChild = (index: number) => {
    const room = rooms[index];
    if (room.childrenAges.length >= MAX_CHILDREN_PER_ROOM) return;
    updateRoom(index, { childrenAges: [...room.childrenAges, DEFAULT_CHILD_AGE] });
  };
  const removeChild = (index: number, childIndex: number) => {
    const room = rooms[index];
    updateRoom(index, { childrenAges: room.childrenAges.filter((_, i) => i !== childIndex) });
  };
  const updateChildAge = (index: number, childIndex: number, age: number) => {
    const room = rooms[index];
    const clamped = Math.max(0, Math.min(17, age));
    updateRoom(index, {
      childrenAges: room.childrenAges.map((a, i) => (i === childIndex ? clamped : a)),
    });
  };

  const totalAdults = rooms.reduce((sum, r) => sum + r.adults, 0);
  const totalChildren = rooms.reduce((sum, r) => sum + r.childrenAges.length, 0);
  const selectedCountry = COUNTRIES.find((c) => c.code.toLowerCase() === residency);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      // La select nazionalità (Radix Popover) renderizza il suo contenuto in un
      // portal fuori da guestsRef: non trattare i click al suo interno come "outside".
      if (
        target instanceof Element &&
        target.closest("[data-radix-popper-content-wrapper]")
      ) {
        return;
      }
      if (guestsRef.current && !guestsRef.current.contains(target)) {
        setGuestsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // I valori selezionabili arrivano dal server (facets): sono calcolati
  // sull'intero set di risultati della ricerca. Ricavarli da `results`, come
  // si faceva prima, oggi significherebbe leggerli dalla sola pagina visibile
  // e vedere le caselle cambiare girando pagina.
  const availableKinds = useMemo(
    () => [...facets.kinds].sort((a, b) => kindLabel(a).localeCompare(kindLabel(b))),
    [facets.kinds],
  );

  // `results` è già la pagina definitiva: filtrata, ordinata e tagliata dal
  // server. Qui non si tocca più nulla.

  // "Ha cercato" non si può più dedurre da results.length: con i filtri
  // server-side una ricerca valida può legittimamente restituire zero
  // risultati, e dedurne "non ha ancora cercato" mostrerebbe lo stato vuoto
  // iniziale al posto del "nessun risultato per questi filtri".
  const hasSearched = (totalHotels !== null && totalHotels !== undefined) || searchError !== null;

  const hasActiveFilters =
    !!filter.min_star_rating ||
    (filter.kind?.length ?? 0) > 0 ||
    (filter.meal_type?.length ?? 0) > 0 ||
    (filter.amenities?.length ?? 0) > 0 ||
    filter.price_from !== undefined ||
    filter.price_to !== undefined ||
    !!filter.name?.trim();

  const resetFilters = () => onFilterChange({});

  // Conteggio da mostrare: le strutture che superano i filtri correnti.
  // Durante una ricerca in volo resta l'ultimo valore noto, così il numero non
  // sfarfalla a ogni carattere digitato.
  const shownCount = totalFiltered ?? results.length;

  return (
    <div className="flex flex-col h-full">
      {/* ── Search bar ── */}
      <div className="border-b bg-gray-50 px-4 py-3 flex-shrink-0">
        <div className="flex flex-wrap items-end gap-3">
          {/* Destination */}
          <div className="flex-1 min-w-[150px]">
            <Label className="text-sm font-medium mb-1 block">{t('ratehawk.destination')}</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                {coordinates ? (
                  /* Destinazione scelta: stessa pillola in entrambe le modalità.
                     Le coordinate stanno sempre DENTRO al campo, così l'altezza
                     della barra di ricerca non cambia mai. Varia solo ciò che le
                     precede: il nome della città in region, l'etichetta
                     "personalizzate" in geo (la geocodifica inversa è fuori scope).
                     Le 2 cifre sono solo visualizzazione — in stato c'è la piena
                     precisione restituita da Leaflet. */
                  <div className="flex h-10 items-center gap-2 rounded-md border border-input bg-background px-3">
                    {searchMode === "region" ? (
                      <>
                        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                          {cityName}
                        </span>
                        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                          {coordinates.lat.toFixed(2)}, {coordinates.lng.toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <>
                        <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="flex-1 truncate font-mono text-sm tabular-nums text-foreground">
                          {t('ratehawk.customCoordinatesLabel', {
                            lat: coordinates.lat.toFixed(2),
                            lng: coordinates.lng.toFixed(2),
                          })}
                        </span>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={onClearDestination}
                      aria-label={t('ratehawk.clearDestination')}
                      className="shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <CityAutocomplete
                    value={cityName}
                    onChange={(v) => {
                      setCityName(v);
                      if (!v) onClearDestination();
                    }}
                    onPlaceSelect={onSelectRegion}
                    placeholder={t('ratehawk.searchCityPlaceholder')}
                  />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0"
                onClick={() => setIsMapOpen(true)}
                title={t('ratehawk.pickOnMap')}
                aria-label={t('ratehawk.pickOnMap')}
              >
                <Globe className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Check-in */}
          <div className="w-[150px]">
            <Label className="text-sm font-medium mb-1 block">{t('ratehawk.checkIn')}</Label>
            <DatePicker
              value={checkIn}
              onSelect={setCheckIn}
              placeholder="Check-in"
              minDate={startDate ? new Date(startDate) : undefined}
              maxDate={endDate ? new Date(endDate) : undefined}
            />
          </div>

          {/* Check-out */}
          <div className="w-[150px]">
            <Label className="text-sm font-medium mb-1 block">{t('ratehawk.checkOut')}</Label>
            <DatePicker
              value={checkOut}
              onSelect={setCheckOut}
              placeholder="Check-out"
              minDate={checkIn ? new Date(checkIn) : startDate ? new Date(startDate) : undefined}
              maxDate={endDate ? new Date(endDate) : undefined}
            />
          </div>

          {/* Guests & rooms dropdown */}
          <div className="flex-1 min-w-[200px] max-w-[350px]" ref={guestsRef}>
            <Label className="text-sm font-medium mb-1 block">{t('ratehawk.guests')}</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setGuestsOpen((o) => !o)}
                className="flex items-center gap-1.5 w-full h-9 px-2.5 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
              >
                <Users className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span className="flex-1 text-left truncate">
                  {totalChildren > 0
                    ? `${totalAdults + totalChildren} (${totalAdults}+${totalChildren})`
                    : `${totalAdults} adult${totalAdults !== 1 ? "s" : ""}`}
                  {" · "}
                  {rooms.length} {rooms.length === 1 ? t('ratehawk.room') : t('ratehawk.rooms')}
                  {selectedCountry && <> · {selectedCountry.flag} {selectedCountry.name}</>}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              </button>
              {guestsOpen && (
                <div className="absolute z-50 top-full mt-1 left-0 bg-white border rounded-md shadow-lg p-3 w-[300px] flex flex-col gap-4 max-h-[420px] overflow-y-auto">
                  {rooms.map((room, roomIndex) => (
                    <div key={roomIndex} className="flex flex-col gap-2 pb-3 border-b last:border-b-0 last:pb-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">{t('ratehawk.room')} {roomIndex + 1}</span>
                        {rooms.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRoom(roomIndex)}
                            className="text-gray-400 hover:text-red-600"
                            title={t('ratehawk.removeRoom')}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm">{t('ratehawk.adults')}</span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => updateRoom(roomIndex, { adults: Math.max(1, room.adults - 1) })}
                            className="h-6 w-6 rounded border flex items-center justify-center hover:bg-gray-100"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-5 text-center">{room.adults}</span>
                          <button
                            type="button"
                            onClick={() => updateRoom(roomIndex, { adults: Math.min(MAX_ADULTS_PER_ROOM, room.adults + 1) })}
                            className="h-6 w-6 rounded border flex items-center justify-center hover:bg-gray-100"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          {t('ratehawk.children')}{" "}
                          <span className="text-xs text-gray-400">({t('ratehawk.childrenAgeRange')})</span>
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => removeChild(roomIndex, room.childrenAges.length - 1)}
                            disabled={room.childrenAges.length === 0}
                            className="h-6 w-6 rounded border flex items-center justify-center hover:bg-gray-100 disabled:opacity-40"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-5 text-center">{room.childrenAges.length}</span>
                          <button
                            type="button"
                            onClick={() => addChild(roomIndex)}
                            disabled={room.childrenAges.length >= MAX_CHILDREN_PER_ROOM}
                            className="h-6 w-6 rounded border flex items-center justify-center hover:bg-gray-100 disabled:opacity-40"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>

                      {room.childrenAges.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {room.childrenAges.map((age, childIndex) => (
                            <label key={childIndex} className="flex items-center gap-1 text-xs text-gray-500">
                              {t('ratehawk.childAge')} {childIndex + 1}
                              <Input
                                type="number"
                                min={0}
                                max={17}
                                value={age}
                                onChange={(e) => updateChildAge(roomIndex, childIndex, parseInt(e.target.value) || 0)}
                                className="w-14 h-7 text-center px-1"
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addRoom}
                    disabled={rooms.length >= MAX_ROOMS}
                    className="flex items-center justify-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-40 disabled:hover:text-blue-600"
                  >
                    <Plus className="h-3.5 w-3.5" /> {t('ratehawk.addRoom')}
                  </button>

                  <div className="border-t pt-3 flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600">{t('ratehawk.nationality')}</span>
                    <span className="text-[10px] text-gray-400">{t('ratehawk.nationalityHint')}</span>
                    <AutocompleteSelect
                      options={COUNTRIES.map((c) => ({ value: c.code.toLowerCase(), label: c.name, icon: c.flag }))}
                      value={residency}
                      onValueChange={onResidencyChange}
                      placeholder={t('ratehawk.selectNationality')}
                      searchPlaceholder={t('ratehawk.searchNationality')}
                      filterMode="includes"
                      maxHeight="200px"
                      side="top"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search button */}
          <Button
            onClick={onSearch}
            disabled={!canSearch || isSearching}
            className="h-9"
            size="sm"
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-1.5">{t('ratehawk.search')}</span>
          </Button>
        </div>

        {cityName && !coordinates && (
          <p className="text-sm text-amber-600 mt-1.5">
            Select a city from the suggestions to get coordinates
          </p>
        )}
      </div>

      {/* ── Body: sidebar + results ── */}
      <div className="relative flex flex-1 overflow-hidden min-h-0">
        {/* Left sidebar skeleton — first search only */}
        {isSearching && !hasSearched && (
          <aside className="hidden sm:flex w-56 flex-shrink-0 border-r bg-gray-50 overflow-y-auto flex-col">
            <div className="px-4 py-3 border-b">
              <Skeleton className="h-4 w-12" />
            </div>
            <div className="px-4 py-4 flex flex-col gap-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-7 w-full" />
                  {i > 0 && <Skeleton className="h-4 w-full" />}
                  {i > 1 && <Skeleton className="h-4 w-4/5" />}
                </div>
              ))}
            </div>
          </aside>
        )}

        {/* Left sidebar — visible only after first search */}
        {hasSearched && (
          <>
            {isFiltersOpen && (
              <div
                className="sm:hidden absolute inset-0 z-30 bg-black/40"
                onMouseDown={() => setIsFiltersOpen(false)}
              />
            )}
          <aside
            className={`flex-shrink-0 border-r bg-gray-50 overflow-y-auto flex flex-col ${
              isFiltersOpen
                ? "absolute left-0 top-0 bottom-0 z-40 w-64 shadow-xl sm:relative sm:w-56 sm:shadow-none"
                : "hidden sm:flex sm:w-56"
            }`}
          >
            <div className="px-4 py-3 flex items-center justify-between border-b">
              <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{t('ratehawk.filters')}</span>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <RotateCcw className="h-3 w-3" />
                    {t('ratehawk.resetFilters')}
                  </button>
                )}
                <button
                  onClick={() => setIsFiltersOpen(false)}
                  className="sm:hidden text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="px-4 py-4 flex flex-col gap-6">
              {/* Hotel name filter */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.hotelName')}</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <Input
                    value={filter.name ?? ""}
                    onChange={(e) => setNameFilter(e.target.value)}
                    placeholder={t('ratehawk.filterByName')}
                    className="pl-7 h-7 text-sm"
                  />
                  {filter.name && (
                    <button
                      onClick={() => setNameFilter("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Min star rating */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.minRating')}</Label>
                <div className="flex gap-1">
                  {([1, 2, 3, 4, 5] as const).map((star) => (
                    <button
                      key={star}
                      onClick={() => setMinStarRating(star)}
                      className="focus:outline-none"
                      title={`${star}+ stelle`}
                    >
                      <Star
                        className={`h-5 w-5 transition-colors ${
                          filter.min_star_rating && star <= filter.min_star_rating
                            ? "text-amber-400 fill-amber-400"
                            : "text-gray-300 fill-gray-100"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {filter.min_star_rating && (
                  <p className="text-xs text-gray-400 mt-1">{filter.min_star_rating}+ stelle</p>
                )}
                {/* Senza questa casella un minimo di stelle nasconderebbe in
                    silenzio appartamenti, ville e boutique, che in ETG hanno
                    rating 0 perché non classificati — non perché scadenti. */}
                <label className="flex items-center gap-2 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    checked={filter.include_unrated !== false}
                    onChange={(e) =>
                      onFilterChange({ ...filter, include_unrated: e.target.checked })
                    }
                    className="rounded"
                  />
                  <span className="text-xs text-gray-600">
                    {t('ratehawk.includeUnrated')}
                  </span>
                </label>
              </div>

              {/* Price range (server-side) */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.priceRange')}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    placeholder="Min €"
                    value={filter.price_from ?? ""}
                    onChange={(e) => onFilterChange({ ...filter, price_from: e.target.value ? Number(e.target.value) : undefined })}
                    className="h-7 text-sm"
                  />
                  <span className="text-gray-400 text-sm">–</span>
                  <Input
                    type="number"
                    min={0}
                    placeholder="Max €"
                    value={filter.price_to ?? ""}
                    onChange={(e) => onFilterChange({ ...filter, price_to: e.target.value ? Number(e.target.value) : undefined })}
                    className="h-7 text-sm"
                  />
                </div>
              </div>

              {/* Hotel type / kind — caselle ricavate dai risultati */}
              {availableKinds.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.propertyType')}</Label>
                  <div className="flex flex-col gap-1.5">
                    {availableKinds.map((k) => (
                      <label key={k} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(filter.kind ?? []).includes(k)}
                          onChange={() => toggleKind(k)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">{kindLabel(k)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Board type / meal (server-side) */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.boardType')}</Label>
                <div className="flex flex-col gap-1.5">
                  {MEAL_OPTIONS.map((m) => (
                    <label key={m.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filter.meal_type ?? []).includes(m.value)}
                        onChange={() => toggleMeal(m.value)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>

            </div>
          </aside>
          </>
        )}

        {/* Results area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4">
            {!hasSearched && !isSearching && (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
                <MapPin className="h-12 w-12 text-gray-200" />
                <p className="">{t('ratehawk.enterDestination')}</p>
              </div>
            )}

            {/* Results header — sempre visibile durante e dopo la ricerca */}
            {(isSearching || (hasSearched && !searchError)) && (
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                {isSearching ? (
                  <Skeleton className="h-4 w-48" />
                ) : (
                  <p className="text-gray-600">
                    {shownCount}{" "}
                    {shownCount === 1 ? "property" : "properties"}
                    {/* Il totale prima dei filtri: ora è l'intera ricerca, non
                        i primi 200 risultati caricati. */}
                    {totalHotels != null && shownCount < totalHotels && (
                      <span className="text-gray-400"> (filtered from {totalHotels})</span>
                    )}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  {hasSearched && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="sm:hidden h-8 gap-1.5"
                      onClick={() => setIsFiltersOpen(true)}
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      {t('ratehawk.filters')}
                      {hasActiveFilters && (
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                      )}
                    </Button>
                  )}
                  <SortResultsButton
                    sortField={sort.field}
                    sortDir={sort.dir}
                    onSortChange={(field, dir) => onSortChange({ field, dir })}
                    disabled={isSearching || shownCount === 0}
                  />
                </div>
              </div>
            )}

            {isSearching && (
              <div className="flex flex-col gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SearchResultCardSkeleton key={i} />
                ))}
              </div>
            )}

            {searchError && !isSearching && (
              <div className="flex flex-col items-center justify-center py-20">
                <X className="h-10 w-10 text-red-400 mb-4" />
                <p className="text-red-600 font-medium">{t('ratehawk.searchFailed')}</p>
                <p className="text-gray-500 mt-1">{searchError}</p>
              </div>
            )}

            {hasSearched && !isSearching && !searchError && (
              <>
                {results.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    {t('ratehawk.noHotelsMatch')}
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {results.map((result) => (
                      <SearchResultCard
                        key={result.hid}
                        result={result}
                        onSelect={onSelectResult}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination footer - fixed.
              Resta montato anche mentre una pagina sta caricando: sparire e
              ricomparire a ogni click farebbe saltare il layout sotto il dito.
              Le pagine sono 1-based, come le conta il server. */}
          {hasSearched && !searchError && (
            <div className="border-t bg-white px-4 py-3 flex items-center justify-center gap-3 flex-shrink-0">
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value) as RateHawkPageSize)}
                disabled={isSearching}
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-600"
                aria-label={t('ratehawk.resultsPerPage')}
              >
                {RATEHAWK_PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} / {t('ratehawk.perPage')}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isSearching || page <= 1}
                  onClick={() => onPageChange(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-gray-600 min-w-[4rem] text-center">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isSearching || page >= totalPages}
                  onClick={() => onPageChange(page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Picker coordinate: montato solo all'apertura, cosi` il chunk leaflet
          non viene nemmeno scaricato da chi non lo usa. */}
      <Modal
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        title={t('ratehawk.pickOnMap')}
        size="lg"
      >
        <Suspense
          fallback={
            /* Stessa altezza del picker: evita il salto di layout quando il
               chunk leaflet finisce di caricare. */
            <div className="flex h-[clamp(280px,52vh,520px)] items-center justify-center px-6 py-5">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          }
        >
          <CoordinatePickerMap
            initialCenter={coordinates}
            initialRadius={radius}
            onCancel={() => setIsMapOpen(false)}
            onConfirm={(coords, newRadius) => {
              setRadius(newRadius);
              onSelectCustomCoordinates(coords);
              setIsMapOpen(false);
            }}
          />
        </Suspense>
      </Modal>
    </div>
  );
}
